var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { createContext, useContext, useMemo, useState } from "react";
import { FloatingFocusManager } from "@floating-ui/react";
import { StyledCloseButtonContainer, StyledModalDialog, StyledModalOverlay } from "./Modal.style";
import useUniqueId from "../../hooks/useUniqueId";
import ButtonIcon from "../button-icon/ButtonIcon";
import useLockBodyScroll from "../../hooks/useLockBodyScroll";
import { GlobalStyle } from "../../theme/GlobalStyle";
import usePortal from "../../hooks/usePortal";
import useIdAttrs from "../../hooks/useIdAttrs";
import useReturnFocus from "../../hooks/useReturnFocus";
import FloatingPortalContainer from "../../hoc/FloatingPortalContainer";
import useAutoFocus from "../../hooks/useAutoFocus";
const idPrefix = "nx-modal-";
export const ModalContext = createContext({ contentRef: null });
export const useModalContext = () => useContext(ModalContext);
const Modal = (_a) => {
    var { width, height, isOpen, children, id: defaultId, "data-insights": insightsAttr, displayCloseIcon = false, onClose, closeOnClickOutside = false, variant = "default" } = _a, rest = __rest(_a, ["width", "height", "isOpen", "children", "id", "data-insights", "displayCloseIcon", "onClose", "closeOnClickOutside", "variant"]);
    useLockBodyScroll(isOpen);
    useReturnFocus(isOpen);
    const id = useUniqueId(idPrefix, defaultId);
    const idAttrs = useIdAttrs({ id, insightsAttr });
    const [contentRef, setContentRef] = useState(null);
    const { refs, context, getPortalProps } = usePortal({
        visible: isOpen,
        onVisibleChange: onClose,
        trigger: "modal",
        portalRef: contentRef,
        portalOptions: {
            dialog: { label: "Modal" },
            trapFocus: true,
        },
    });
    const handleClose = () => {
        if (onClose) {
            onClose();
        }
    };
    useAutoFocus({ current: contentRef }, isOpen && contentRef !== null && !!document.activeElement && !contentRef.contains(document.activeElement));
    const contextValue = useMemo(() => ({ contentRef }), [contentRef]);
    const content = (React.createElement(StyledModalOverlay, { onClick: closeOnClickOutside ? handleClose : undefined },
        React.createElement(FloatingFocusManager, { context: context, initialFocus: refs.floating },
            React.createElement(StyledModalDialog, Object.assign({}, idAttrs(), { width: width, height: height, variant: variant, ref: setContentRef, "aria-modal": "true", onClick: (e) => e.stopPropagation() }, getPortalProps(), rest),
                React.createElement(GlobalStyle, null,
                    React.createElement(ModalContext.Provider, { value: contextValue }, children),
                    displayCloseIcon && (React.createElement(StyledCloseButtonContainer, { variant: variant },
                        React.createElement(ButtonIcon, Object.assign({}, idAttrs("-close"), { "aria-label": "Close", iconName: "close", onClick: handleClose })))))))));
    return isOpen ? React.createElement(FloatingPortalContainer, { content: content }) : null;
};
export default Modal;
//# sourceMappingURL=Modal.js.map